// Type-only: inventorySlice imports this module at runtime, so a value import
// here would close the cycle.
import type { InventoryItem } from '../redux/inventorySlice';
import { cleanPantryName } from './pantryFoodName';
import { findDuplicate } from './pantryDuplicates';
import { stapleKeysForLine, assumedOnHand } from '../constants/staples';

/**
 * What shelving one line would do, before anything is written.
 *
 * The reducer acts on it and the thunk reports it, so a caller can say WHY a
 * line was not shelved. Before #796 every refusal read « déjà au garde-manger »:
 * the recipe screen said "buy bay leaves", « Je l'ai » was refused because the
 * pantry called them a staple, and the toast claimed a row that did not exist.
 */
export type PantryAddDecision =
  | { kind: 'add'; name: string }
  /** Not a food the pantry can hold: water, a skewer, debris from a parse. */
  | { kind: 'skip'; reason: 'unusable' }
  /** Assumed on hand (and not turned off in Basiques), so never tracked. */
  | { kind: 'skip'; reason: 'staple'; stapleKey: string }
  /** The pantry already holds this food, under `existing`. */
  | { kind: 'skip'; reason: 'duplicate'; existing: string };

export type PantryAddSkip = Extract<PantryAddDecision, { kind: 'skip' }>;

export function decidePantryAdd(
  raw: string,
  items: InventoryItem[],
  excludedStaples: readonly string[] = []
): PantryAddDecision {
  if (!raw || !raw.trim()) return { kind: 'skip', reason: 'unusable' };

  // Reduce the recipe line to the food it names BEFORE anything else looks at
  // it — see utils/pantryFoodName.
  const cleaned = cleanPantryName(raw);
  if (!cleaned.usable) return { kind: 'skip', reason: 'unusable' };

  // Staples never reach the grocery list and already count as "have" on the
  // recipe screen, so a row tracking one is noise. The question is the one both
  // of those ask, about the same line: a row the recipe screen offers to shelve
  // (it said « à acheter ») is by construction not a staple here. An excluded
  // staple is shelved like anything else — that is what lets the grocery list's
  // exclusion clear once the cook has bought it.
  const stapleKeys = stapleKeysForLine(raw);
  if (assumedOnHand(stapleKeys, excludedStaples)) {
    return { kind: 'skip', reason: 'staple', stapleKey: stapleKeys[0] };
  }

  // One matcher, shared with the camera/receipt path.
  const duplicate = findDuplicate(cleaned.name, items);
  if (duplicate) return { kind: 'skip', reason: 'duplicate', existing: duplicate.name };

  return { kind: 'add', name: cleaned.name };
}
